import type { ActivationView } from "@/lib/api/onboarding-activation"

// Client-side PDF of the signed Data Authorization — the customer's copy,
// generated in the browser (jspdf, lazy-loaded so the flow bundle stays
// light). Mirrors what they saw on screen: full verbatim terms, then the
// signature block. No server round-trip, no new endpoint.

const PINE: [number, number, number] = [24, 83, 65]
const EMERALD: [number, number, number] = [15, 163, 107]
const INK: [number, number, number] = [17, 24, 21]
const BODY: [number, number, number] = [60, 72, 66]
const MUTED: [number, number, number] = [130, 142, 136]

export async function downloadSignedPdf(data: ActivationView) {
  const { jsPDF } = await import("jspdf")
  const doc = new jsPDF({ unit: "pt", format: "letter" })
  const W = doc.internal.pageSize.getWidth()
  const H = doc.internal.pageSize.getHeight()
  const M = 64
  const MAXW = W - M * 2
  let y = M

  const guard = (needed: number) => {
    if (y + needed > H - M) {
      doc.addPage()
      y = M
    }
  }

  // brand header
  doc.setFont("helvetica", "bold").setFontSize(22).setTextColor(...PINE)
  doc.text("eTX", M, y)
  const etxW = doc.getTextWidth("eTX ")
  doc.setFontSize(13).setTextColor(43, 43, 43)
  doc.text("ImpaXt", M + etxW, y)
  doc.setFont("helvetica", "normal").setFontSize(6.5).setTextColor(...EMERALD)
  doc.text("C O L L I S I O N   ·   A I   ·   I N T E L L I G E N C E", M, y + 12)
  doc.setDrawColor(...EMERALD).setLineWidth(1.5)
  doc.line(M, y + 24, W - M, y + 24)
  y += 52

  // title + meta
  doc.setFont("helvetica", "bold").setFontSize(15).setTextColor(...INK)
  doc.text(`${data.agreement.title} — Terms and Conditions`, M, y)
  y += 16
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...MUTED)
  const effective = new Date(data.agreement.effectiveDate).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
  doc.text(`Version ${data.agreement.version} · Effective ${effective}`, M, y)
  y += 14

  const d = data.businessDetails
  const addr = [d.street, d.city, d.state, d.zipCode, d.country].filter(Boolean).join(", ")
  doc.setFontSize(9.5).setTextColor(...BODY)
  doc.text(
    doc.splitTextToSize(`Signed for: ${data.shop.legalBusinessName}${addr ? ` · ${addr}` : ""}`, MAXW),
    M,
    y,
  )
  y += 26

  // the terms, verbatim
  for (const para of data.agreement.body) {
    if (para.startsWith("## ")) {
      guard(30)
      y += 6
      doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(...INK)
      doc.text(para.slice(3), M, y)
      y += 15
      continue
    }
    doc.setFont("helvetica", "normal").setFontSize(9.5).setTextColor(...BODY)
    const lines = doc.splitTextToSize(para, MAXW) as string[]
    guard(lines.length * 12 + 8)
    doc.text(lines, M, y, { lineHeightFactor: 1.35 })
    y += lines.length * 12.8 + 8
  }

  // signature block
  guard(120)
  y += 14
  doc.setDrawColor(200, 210, 204).setLineWidth(0.75)
  doc.line(M, y, W - M, y)
  y += 24
  doc.setFont("helvetica", "normal").setFontSize(8.5).setTextColor(...MUTED)
  doc.text("SIGNED ELECTRONICALLY BY", M, y)
  y += 22
  doc.setFont("times", "bolditalic").setFontSize(24).setTextColor(...INK)
  doc.text(data.agreement.signerName ?? "", M, y)
  const now = new Date()
  const signedOn = now.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...MUTED)
  doc.text(signedOn, W - M, y, { align: "right" })
  y += 12
  doc.setDrawColor(...PINE).setLineWidth(1)
  doc.line(M, y, W - M, y)
  y += 16
  doc.setFontSize(8).setTextColor(...MUTED)
  doc.text(
    doc.splitTextToSize(
      `On behalf of ${data.shop.legalBusinessName}. Electronic signature under the U.S. ESIGN Act and UETA — agreement version ${data.agreement.version}, document generated ${signedOn}.`,
      MAXW,
    ),
    M,
    y,
  )

  const slug = data.shop.legalBusinessName.replace(/[^\w]+/g, "-").replace(/^-+|-+$/g, "")
  doc.save(`eTX-Data-Authorization-${slug || "signed"}.pdf`)
}
